import { memo, useCallback } from "react";
import ModalWrapper from "..";
import { useTranslation } from "react-i18next";
import { Button } from "../../button";
import toast from "react-hot-toast";
import { useCloseEvacuationEvent } from "../../../hooks/emergency-evacuation/mutation";
import { CircleAlert } from "lucide-react";
import * as Yup from "yup";
import i18n from "../../../i18n";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../../inputs/TextInput";

interface CloseEvacuationEventModalProps {
  eventId: number | null;
  isOpen: boolean;
  onOpenChange: () => void;
}

const CloseEvacuationEventModalSchema = Yup.object({
  reason: Yup.string().required(
    i18n.language === "en" ? "Reason is required." : "السبب مطلوب.",
  ),
});

const CloseEvacuationEventModal = ({
  eventId,
  isOpen,
  onOpenChange,
}: CloseEvacuationEventModalProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useFormWithSchema(CloseEvacuationEventModalSchema);

  const {
    mutateAsync: CloseEvacuationEvent,
    isPending: isCloseEvacuationEventLoading,
  } = useCloseEvacuationEvent();

  const onClose = useCallback(() => {
    setValue("reason", "");
    onOpenChange();
  }, [onOpenChange, setValue]);

  const onCloseEvent = useCallback(
    async (data: { reason: string }) => {
      if (!eventId) return;

      try {
        const body = {
          n_EvacuationID: eventId,
          s_Reason: data.reason,
        };

        await CloseEvacuationEvent(body);
        toast.success(t("evacuationEventClosedSuccessfully"));
      } catch (error: any) {
        toast.error(t("somethingWentWrong"));
      } finally {
        onClose();
      }
    },
    [CloseEvacuationEvent, onClose, eventId, t],
  );

  return (
    <ModalWrapper isOpen={isOpen} onOpenChange={onClose}>
      <form
        onSubmit={handleSubmit(onCloseEvent)}
        className=" flex flex-col items-center justify-center gap-5 w-full"
      >
        <div className="flex flex-col items-center justify-center gap-1">
          {/* <div className="flex items-center gap-2">
            <CircleAlert className="w-4 sm:w-5 text-red-600" />
            <p className="sm:text-lg font-medium text-red-600">
              {t("thereAreMissingParticipants")}
            </p>
          </div> */}
          <p className="sm:text-lg font-medium">
            {t("confirmCloseEvacuationEvent")}
          </p>
        </div>

        <div className="w-full px-3 sm:w-1/2">
          <Controller
            name="reason"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("reason")}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                value={value}
              />
            )}
          />
        </div>

        <div className="flex gap-2 items-center">
          <Button isLoading={isCloseEvacuationEventLoading}>
            {t("yesClose")}
          </Button>
          <Button
            onClick={onClose}
            className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
          >
            {t("cancel")}
          </Button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default memo(CloseEvacuationEventModal);
